import { fromJS, List, Map } from "immutable";
import subscriptionConstants from "../constants/subscriptionConstants";

export const initialState = Map({
	list: List(),
	meta: Map(),
	stats: Map(),
	filters: Map({
		search: "",
		status: List(),
		serviceId: null,
		financialImpactId: null,
		invoicingPeriodId: null,
		durationId: null,
	}),
	queryParameter: Map({
		page: 1,
		search: "",
		status: List(),
		serviceId: null,
		financialImpactId: null,
		invoicingPeriodId: null,
		durationId: null,
	}),
	metadata: Map({
		service: List(),
		financial_impact: List(),
		invoicing_period: List(),
		subscription_duration: List(),
	}),
	loading: Map({
		list: false,
		create: false,
		update: false,
		delete: false,
		metadata: Map(),
		stats: false,
	}),
	current: Map(),
	action: false,
	request: false,
	requestMessage: "",
	successMessage: "",
	errorMessage: "",
	error: null,
});

const updateListWithEntity = (list, entity) => {
	const immutableList = List.isList(list) ? list : List();
	const nextItem = fromJS(entity);
	const idx = immutableList.findIndex((item) => item.get("id") === entity.id);
	if (idx >= 0) {
		return immutableList.set(idx, nextItem);
	}
	return immutableList.unshift(nextItem);
};

const subscriptionReducer = (state = initialState, action = {}) => {
	switch (action.type) {
		case subscriptionConstants.SUBSCRIPTIONS_LIST_REQUEST:
			return state.setIn(["loading", "list"], true).set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_LIST_SUCCESS:
			return state
				.set("list", fromJS(action.payload?.list || []))
				.set("meta", fromJS(action.payload?.meta || {}))
				.setIn(["loading", "list"], false)
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_LIST_FAILURE:
			return state
				.setIn(["loading", "list"], false)
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_CREATE_REQUEST:
			return state
				.setIn(["loading", "create"], true)
				.set("error", null)
				.set("action", false)
				.set("request", false)
				.set("requestMessage", "")
				.set("successMessage", "")
				.set("errorMessage", "");

		case subscriptionConstants.SUBSCRIPTIONS_CREATE_SUCCESS:
			return state
				.update("list", (list) =>
					updateListWithEntity(list, action.payload || {})
				)
				.setIn(["loading", "create"], false)
				.set("error", null)
				.set("action", true)
				.set("request", true)
				.set("successMessage", action.message || "created")
				.set("requestMessage", action.message || "created")
				.set("errorMessage", "");

		case subscriptionConstants.SUBSCRIPTIONS_CREATE_FAILURE:
			return state
				.setIn(["loading", "create"], false)
				.set("action", false)
				.set("request", true)
				.set("requestMessage", action.error || "Failed to create")
				.set("errorMessage", action.error || "Failed to create")
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_SET_SUCCESS_MESSAGE:
			return state.set("successMessage", action.data || "");

		case subscriptionConstants.SUBSCRIPTIONS_ACTION_SUCCESS:
			return state
				.set("action", true)
				.set("request", true)
				.set("successMessage", action.message || "")
				.set("requestMessage", action.message || "")
				.set("errorMessage", "")
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_ACTION_FAILURE:
			return state
				.set("action", false)
				.set("request", true)
				.set("requestMessage", action.error || "")
				.set("errorMessage", action.error || "")
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_CLEAR_REQUEST_STATE:
			return state
				.set("action", false)
				.set("request", false)
				.set("requestMessage", "")
				.set("successMessage", "")
				.set("errorMessage", "")
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_UPDATE_REQUEST:
			return state
				.setIn(["loading", "update"], true)
				.set("error", null)
				.set("action", false)
				.set("request", false)
				.set("requestMessage", "")
				.set("successMessage", "")
				.set("errorMessage", "");

		case subscriptionConstants.SUBSCRIPTIONS_UPDATE_SUCCESS:
			return state
				.update("list", (list) =>
					action.payload ? updateListWithEntity(list, action.payload) : list
				)
				.setIn(["loading", "update"], false)
				.set("error", null)
				.set("action", true)
				.set("request", true)
				.set("requestMessage", action.message || "")
				.set("successMessage", action.message || "")
				.set("errorMessage", "");

		case subscriptionConstants.SUBSCRIPTIONS_UPDATE_FAILURE:
			return state
				.setIn(["loading", "update"], false)
				.set("action", false)
				.set("request", true)
				.set("requestMessage", action.error || "Failed to update")
				.set("errorMessage", action.error || "Failed to update")
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_FILTERS_UPDATE:
			return state.mergeIn(["filters"], fromJS(action.payload || {}));

		case subscriptionConstants.SUBSCRIPTIONS_SET_CURRENT:
			return state
				.set("current", fromJS(action.payload || {}))
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_CLEAR_CURRENT:
			return state.set("current", Map());

		case subscriptionConstants.SUBSCRIPTIONS_METADATA_REQUEST:
			return state
				.setIn(["loading", "metadata", action.metaType], true)
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_METADATA_SUCCESS:
			return state
				.setIn(["metadata", action.metaType], fromJS(action.payload || []))
				.setIn(["loading", "metadata", action.metaType], false);

		case subscriptionConstants.SUBSCRIPTIONS_METADATA_FAILURE:
			return state
				.setIn(["loading", "metadata", action.metaType], false)
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_STATS_REQUEST:
			return state.setIn(["loading", "stats"], true).set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_STATS_SUCCESS:
			return state
				.set("stats", fromJS(action.payload || {}))
				.setIn(["loading", "stats"], false)
				.set("error", null);

		case subscriptionConstants.SUBSCRIPTIONS_STATS_FAILURE:
			return state
				.setIn(["loading", "stats"], false)
				.set("error", action.error || null);

		case subscriptionConstants.SUBSCRIPTIONS_RESET_STATUS:
			return state
				.set("error", null)
				.set("successMessage", "")
				.set("errorMessage", "")
				.set("requestMessage", "")
				.set("action", false)
				.set("request", false)
				.setIn(["loading", "create"], false);

		case subscriptionConstants.SUBSCRIPTIONS_REQUEST_DONE:
			return state
				.set("action", false)
				.set("request", false)
				.set("error", null)
				.set("successMessage", "")
				.set("errorMessage", "")
				.set("requestMessage", "");

		case subscriptionConstants.SET_SUBSCRIPTION_QUERY_PARAMETER:
			return state.mergeIn(["queryParameter"], fromJS(action.data || {}));

		case subscriptionConstants.CLEAR_SUBSCRIPTION_QUERY_PARAMETER:
			return state.set("queryParameter", initialState.get("queryParameter"));

		case subscriptionConstants.SUBSCRIPTIONS_CLEAR_FILTERS:
			return state.set("filters", initialState.get("filters"));

		default:
			return state;
	}
};

export default subscriptionReducer;
